import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocalStorage } from "usehooks-ts";
import BIContainer from "@/components/ui/bi/BIContainer";
import DisplayGroupSelect from "@/components/ui/bi/DisplayGroupSelect";
import TitleBar from "@/components/ui/TitleBar";
import useConfig from "@/hooks/useConfig";
import { useDisplayGroups } from "@/hooks/useDisplayGroups";
import { useVocCountData } from "@/hooks/useVocCountData";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { DisplayGroup } from "@/models/displayGroup";

const HomePage: React.FC = () => {
  const { t } = useTranslation("voiceek_ui");
  const { config } = useConfig();
  const { sessionCurrentWorkspaceId } = useWorkspaces();
  const { displayGroups } = useDisplayGroups();
  const { filterByPaths, vocCountData } = useVocCountData();

  const storageKey = `hiddenDisplayGroupsForBI_${sessionCurrentWorkspaceId}`;

  const [hiddenDisplayGroupsForBI, setHiddenDisplayGroupsForBI] = useLocalStorage<string[]>(
    storageKey,
    [],
  );

  // 初回訪問時(localStorageにキーがないとき)のみconfig値を反映
  useEffect(() => {
    if (!config) return;

    const stored = localStorage.getItem(storageKey);
    if (stored !== null) return; // localStorageにキーがある場合は何もしない

    // overviewがデフォルト非表示の場合の処理
    if (config.showOverviewByDefault === false) {
      setHiddenDisplayGroupsForBI(["overview"]);
    }
  }, [config, storageKey, setHiddenDisplayGroupsForBI]);

  // 表示する表示グループのIDリストを計算（非表示リストに含まれていないもの）
  const selectedDisplayGroupsForBI = useMemo(() => {
    const allDisplayGroupIds = ["overview", ...(displayGroups?.map((dg) => dg.id) || [])];
    return allDisplayGroupIds.filter((id) => !hiddenDisplayGroupsForBI.includes(id));
  }, [displayGroups, hiddenDisplayGroupsForBI]);

  if (!displayGroups) return <></>;

  const overviewDisplayGroup = new DisplayGroup("", t("home.title"), 0, []);

  return (
    <>
      <TitleBar title={t("home.title")} />

      <div className="p-8 flex flex-col gap-5 w-full max-w-[1320px]">
        <DisplayGroupSelect
          hidden={hiddenDisplayGroupsForBI}
          onChange={(hiddenIds: string[]) => setHiddenDisplayGroupsForBI(hiddenIds)}
        />
        {vocCountData && selectedDisplayGroupsForBI.includes("overview") && (
          <BIContainer
            key={`${overviewDisplayGroup.id}-${sessionCurrentWorkspaceId}`}
            displayGroup={overviewDisplayGroup}
            vocCountData={vocCountData}
            hideTable
            hideDataVisibilityToggle={true}
          />
        )}
        {displayGroups.map((dg) => {
          if (selectedDisplayGroupsForBI.includes(dg.id)) {
            const data = filterByPaths(dg.categoryPaths);
            return data && (
              <BIContainer key={dg.id} displayGroup={dg} vocCountData={data} hideDataVisibilityToggle={true} />
            );
          }
        })}
      </div>
    </>
  );
};

export default HomePage;
